/**
 * Road closures: the calls, their shapes and their cache. The application's
 * only writes (spec D5).
 *
 * In a module of its own rather than in `client.ts`'s `api`, so that screens
 * which mock `api` in their tests are not changed by it. The transport is the
 * same `request`, so the token, the envelope and the sign-in redirect are
 * still in one place.
 *
 * Shapes read from `api/app/schemas/closures.py` (1 October 2026). Every time
 * carries its offset, in the planner's zone, which the list sends as
 * `time_zone`. Until bsd-knowledge v0.52 is applied every call is refused
 * with 503 `CLOSURES_NOT_AVAILABLE`, and each screen says so in words.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { request } from "./client";

export interface ClosureArea {
  south: number;
  west: number;
  north: number;
  east: number;
}

export type ClosureStatus = "coming" | "active" | "over" | "ended";

export interface Closure {
  closure_id: number;
  /** Where: the road or place, as the dispatcher named it. */
  name: string;
  reason: string | null;
  note_for_drivers: string | null;
  area: ClosureArea;
  /** False when the stored area is not a rectangle; `area` is its bounds. */
  is_rectangle: boolean;
  valid_from: string;
  /** Null: until further notice (C5 B), with `review_on`. */
  valid_to: string | null;
  /** `YYYY-MM-DD`, set only on a closure until further notice. */
  review_on: string | null;
  ended_at: string | null;
  status: ClosureStatus;
  /** V-C5 A: an open-ended closure whose review date has come. Still in force. */
  to_be_checked: boolean;
  recorded_by: string | null;
  recorded_at: string;
  updated_by: string | null;
  updated_at: string;
  ended_by: string | null;
}

export interface ClosureList {
  time_zone: string;
  closures: Closure[];
}

export interface NewClosure {
  name: string;
  reason: string | null;
  note_for_drivers: string | null;
  area: ClosureArea;
  valid_from: string;
  valid_to: string | null;
  review_on?: string | null;
  /** C6 A: the dispatcher has seen which farms the area covers. */
  confirm_farms_inside?: boolean;
}

/** What a rectangle covers, asked while it is drawn (C6, C16). */
export interface Impact {
  farms_inside: { farmer_id: number; farm_name: string }[];
  bases_inside: { location_id: number; name: string }[];
}

/** Only the fields sent change. `end: true` goes alone. `expected_updated_at`
 *  is the copy's `updated_at`, sent back as received, so a change made from
 *  an old copy is refused (409 CLOSURE_CHANGED) instead of overwriting. */
export type ClosureChange = Partial<Omit<NewClosure, "name">> & {
  name?: string;
  end?: true;
  expected_updated_at?: string;
};

export const closuresApi = {
  /** Active and coming. */
  current: () => request<ClosureList>("/api/closures"),
  /** In force at some moment of one local day. */
  on: (date: string) => request<ClosureList>("/api/closures", { on: date }),
  create: (closure: NewClosure) =>
    request<Closure>("/api/closures", {}, { method: "POST", body: closure }),
  change: (closureId: number, change: ClosureChange) =>
    request<Closure>(`/api/closures/${closureId}`, {}, { method: "PATCH", body: change }),
  impact: (area: ClosureArea, from: string | null) =>
    request<Impact>("/api/closures/impact", { ...area, ...(from ? { from } : {}) }),
};

/** Under their own first word, as the runs are under theirs: one write
 *  invalidates every list of closures, the day maps' included. */
export const closureKeys = {
  all: () => ["closures"] as const,
  current: () => ["closures", "current"] as const,
  on: (date: string) => ["closures", "on", date] as const,
  impact: (area: ClosureArea | null, from: string | null) => ["closures", "impact", area, from] as const,
};

/** Asked only once the area is a rectangle; the answer is the API's, which
 *  reads the farms with the planner's own loader. */
export function useImpact(area: ClosureArea | null, from: string | null) {
  return useQuery({
    queryKey: closureKeys.impact(area, from),
    queryFn: () => closuresApi.impact(area!, from),
    enabled: area !== null,
  });
}

export function useCurrentClosures() {
  return useQuery({ queryKey: closureKeys.current(), queryFn: () => closuresApi.current() });
}

export function useClosuresOn(date: string) {
  return useQuery({ queryKey: closureKeys.on(date), queryFn: () => closuresApi.on(date) });
}

export function useRecordClosure() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (closure: NewClosure) => closuresApi.create(closure),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: closureKeys.all() });
    },
  });
}

export function useChangeClosure() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ closureId, change }: { closureId: number; change: ClosureChange }) =>
      closuresApi.change(closureId, change),
    // The API's answer is the closure as it now is: written into every list
    // at once, so a second change made before the refetch lands carries the
    // new updated_at and is not refused as made from an old copy.
    onSuccess: (changed) => {
      client.setQueriesData<ClosureList>({ queryKey: closureKeys.all() }, (list) =>
        list
          ? {
              ...list,
              closures: list.closures.map((c) => (c.closure_id === changed.closure_id ? changed : c)),
            }
          : list,
      );
    },
    // Refetched on a refusal too: a 409 means the copy on screen is old. Not
    // returned: TanStack awaits a returned promise before the caller's own
    // onSuccess, which would hold the screen's "Ended" until the refetch, and
    // lose it when the refetch removes the row first.
    onSettled: () => {
      void client.invalidateQueries({ queryKey: closureKeys.all() });
    },
  });
}
